/**
 * Drives the OAuth consent popup for connecting an external MCP server.
 *
 * The flow crosses two browser contexts and one origin boundary:
 *
 *   1. This window asks the agents service for an authorization URL.
 *   2. It opens that URL in a popup. The user consents at the *third party's*
 *      authorization server — an origin we cannot script.
 *   3. The AS redirects the popup back to our own callback route, which is a
 *      real page in this SPA and therefore same-origin again.
 *   4. That page posts the query parameters back here and closes itself.
 *
 * `postMessage` rather than polling `popup.location`: while the popup sits on
 * the authorization server, reading its location throws a cross-origin error,
 * so there is nothing to poll until the moment it has already come home.
 *
 * The popup is opened *before* the authorize URL is fetched. Browsers only
 * allow `window.open` synchronously inside a user gesture; opening it after an
 * `await` gets it blocked as a pop-up.
 */

const POPUP_FEATURES = 'width=680,height=820,menubar=no,toolbar=no,location=yes'

/** Message type on the postMessage channel; must match the callback view. */
export const MCP_OAUTH_MESSAGE = 'paice:mcp-oauth-callback'

/** Where the callback view stashes its result when postMessage cannot be used. */
export const MCP_OAUTH_STORAGE_KEY = 'paice.mcpOauthResult'

export class McpOAuthCancelled extends Error {
    constructor(message = 'The connection window was closed before it finished.') {
        super(message)
        this.name = 'McpOAuthCancelled'
    }
}

/**
 * Open a popup synchronously, so it survives the browser's pop-up blocker.
 * @returns {Window|null} null when the browser blocked it outright.
 */
export function openOAuthPopup() {
    return window.open('', 'paice-mcp-oauth', POPUP_FEATURES)
}

/**
 * Wait for the callback page to report the authorization response.
 *
 * @param {Window} popup - the window returned by {@link openOAuthPopup}
 * @param {Object} [options]
 * @param {number} [options.timeoutMs=300000] - give up after this long. Sized
 *   for a human getting through a consent screen, and shorter than the pending
 *   credential row's server-side TTL so the failure surfaces here first.
 * @returns {Promise<{code: string, state: string, iss: ?string}>}
 */
export function awaitOAuthCallback(popup, { timeoutMs = 300000 } = {}) {
    return new Promise((resolve, reject) => {
        let settled = false

        const cleanup = () => {
            window.removeEventListener('message', onMessage)
            clearInterval(closedTimer)
            clearTimeout(timeoutTimer)
        }

        const finish = (fn, value) => {
            if (settled) return
            settled = true
            cleanup()
            fn(value)
        }

        const onMessage = (event) => {
            // Only ever trust our own origin. The popup visits a third-party
            // authorization server on the way, and that page can post to this
            // window too — without this check it could hand us an attacker's
            // authorization code to redeem.
            if (event.origin !== window.location.origin) return
            const data = event.data
            if (!data || data.type !== MCP_OAUTH_MESSAGE) return

            if (data.error) {
                finish(reject, new Error(data.errorDescription || data.error))
                return
            }
            finish(resolve, { code: data.code, state: data.state, iss: data.iss || null })
        }

        window.addEventListener('message', onMessage)

        // A user who closes the window is not an error worth a stack trace,
        // but the caller still has to stop showing a spinner.
        const closedTimer = setInterval(() => {
            if (popup && popup.closed) {
                // The close event can beat the message, so give the message
                // loop one more tick before calling it cancelled.
                setTimeout(() => finish(reject, new McpOAuthCancelled()), 250)
            }
        }, 500)

        const timeoutTimer = setTimeout(() => {
            try { if (popup && !popup.closed) popup.close() } catch (e) { /* cross-origin */ }
            finish(reject, new McpOAuthCancelled('The connection timed out. Try again.'))
        }, timeoutMs)
    })
}

/**
 * Run the whole connect: open popup, fetch URL, navigate, await the callback.
 *
 * @param {Object} args
 * @param {Function} args.start - resolves to `{ authorizationUrl, ... }`
 * @returns {Promise<{code: string, state: string, iss: ?string}>}
 */
export async function runOAuthConnect({ start }) {
    const popup = openOAuthPopup()
    if (!popup) {
        throw new Error(
            'Your browser blocked the connection window. Allow pop-ups for this site and try again.',
        )
    }

    let authorizationUrl
    try {
        const result = await start()
        authorizationUrl = result?.authorizationUrl
        if (!authorizationUrl) throw new Error('The service did not return an authorization URL.')
    } catch (e) {
        try { popup.close() } catch (err) { /* already gone */ }
        throw e
    }

    popup.location.href = authorizationUrl
    return awaitOAuthCallback(popup)
}
